import { memo } from 'react';

import { PurchaseInvoiceMonthsInformation } from 'models';

import useTranslation from 'hooks/useTranslation';

import { getMonthsList } from 'utils';
import formatPercent from 'shared/utils/formatPercent';
import formatCurrency from 'shared/utils/formatCurrency';

import { getAccountingYear } from './helpers';

import Stack from '@mui/material/Stack';
import { StyledBox, Label } from '../PurchaseInvoice.style';
import {
  MonthsList,
  MonthItem,
  MonthName,
  MonthAmount,
  Divider,
  Description,
  Percentage,
} from './PurchaseInvoiceMonths.style';
import Fraction from 'shared/components/Fraction';
import Loader from 'components/loader';
import NoData from 'components/no-data';

type PurchaseInvoiceMonthsProps = {
  isLoading: boolean;
  monthsInformation: PurchaseInvoiceMonthsInformation | null;
  sellerOrganizationName: string | null;
};

const monthsList = getMonthsList();

const PurchaseInvoiceMonths = ({
  isLoading,
  monthsInformation,
  sellerOrganizationName,
}: PurchaseInvoiceMonthsProps) => {
  const { t } = useTranslation();

  const accountingYear =
    monthsInformation?.FiscalYearStartDate &&
    monthsInformation.FiscalYearEndDate
      ? getAccountingYear(
          monthsInformation?.FiscalYearStartDate,
          monthsInformation.FiscalYearEndDate
        )
      : null;

  return (
    <StyledBox alignCenter={isLoading || !monthsInformation}>
      {isLoading ? (
        <Loader themeColor="darkBlue" />
      ) : !!monthsInformation ? (
        <>
          {sellerOrganizationName && <Label>{sellerOrganizationName}</Label>}
          <Label>
            {t('#portal.purchaseinvoice.detail.invoice')} {accountingYear}
          </Label>
          <MonthsList>
            {monthsInformation &&
              monthsInformation.Months.map(({ Month, TotalAmount }) => (
                <MonthItem key={Month}>
                  <MonthName>{monthsList[Month - 1]}</MonthName>
                  <MonthAmount>
                    {formatCurrency(TotalAmount?.Value, TotalAmount?.Code)}
                  </MonthAmount>
                </MonthItem>
              ))}
            <Divider sx={{ marginBottom: '10px' }} />
            <MonthItem>
              <MonthName>Yhteensä</MonthName>
              <MonthAmount>
                {formatCurrency(
                  monthsInformation?.SellerTotalAmount?.Value,
                  monthsInformation?.SellerTotalAmount?.Code
                )}
              </MonthAmount>
            </MonthItem>
          </MonthsList>
          {sellerOrganizationName && <Label>{sellerOrganizationName}</Label>}
          <Label>
            {t('#portal.purchaseinvoice.detail.expenditurecomparison')}{' '}
            {accountingYear}
          </Label>
          <Description>
            {t(
              '#portal.purchaseinvoice.detail.shareoftotalpropertymaintenancecosts'
            )}
          </Description>
          <Stack spacing={3} direction="row" alignItems="center">
            <Fraction
              numerator={formatCurrency(
                monthsInformation?.SellerTotalAmount?.Value,
                monthsInformation?.SellerTotalAmount?.Code
              )}
              denominator={formatCurrency(
                monthsInformation?.TotalPropertyMaintenanceCosts?.Value,
                monthsInformation?.TotalPropertyMaintenanceCosts?.Code
              )}
            />
            <Percentage>
              {formatPercent(
                monthsInformation?.SellerToTotalPropertyMaintenanceCostsСorrelationPersent
              )}
            </Percentage>
          </Stack>
        </>
      ) : (
        <NoData />
      )}
    </StyledBox>
  );
};

export default memo(PurchaseInvoiceMonths);
